import React from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { TextField } from '@material-ui/core';

import { FormPhoneInputProps } from 'types';

const FormPhoneInput = (props: FormPhoneInputProps): React.ReactElement => {
  const { t } = useTranslation('dashboard');
  const { name, label, required, errorobj, control, placeholder, testid } = props;
  let errorMessage = '';
  if (errorobj && errorobj[name]) {
    errorMessage = errorobj[name].message;
  }

  const normalizeUSPhoneDigits = (raw: string): string => {
    const digits = (raw || '').replace(/\D/g, '').slice(0, 11);
    if (digits.length === 11 && !digits.startsWith('1')) return digits.slice(0, 10);
    return digits;
  };

  // Display formatting rules:
  // - 10 digits: (200)-102-1020
  // - 11 digits starting with 1: 1-(800)-102-1020
  const formatUSPhoneForDisplay = (rawDigits: string): string => {
    const d = (rawDigits || '').replace(/\D/g, '');
    if (!d) return '';

    // Handle "1" + 10 digits progressively
    if (d.startsWith('1')) {
      if (d.length === 1) return '1';
      if (d.length <= 4) return `1-(${d.slice(1)}`;
      if (d.length <= 7) return `1-(${d.slice(1, 4)})-${d.slice(4)}`;
      return `1-(${d.slice(1, 4)})-${d.slice(4, 7)}-${d.slice(7, 11)}`;
    }
    // Handle 10-digit US format progressively
    if (d.length <= 3) return `(${d}`;
    if (d.length <= 6) return `(${d.slice(0, 3)})-${d.slice(3)}`;
    return `(${d.slice(0, 3)})-${d.slice(3, 6)}-${d.slice(6, 10)}`;
  };

  return (
    <Controller
      name={name}
      control={control}
      defaultValue=""
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      render={({ onChange, onBlur, value, name: fieldName, ref }) => {
        const digits = (value || '').toString();
        const display = formatUSPhoneForDisplay(digits);

        return (
          <TextField
            {...props}
            variant="outlined"
            name={fieldName}
            value={display}
            onBlur={onBlur}
            onFocus={(): void => null}
            label={t(label)}
            placeholder={t(placeholder)}
            error={!!errorMessage}
            helperText={t(errorMessage)}
            InputLabelProps={{
              required: required || false,
            }}
            FormHelperTextProps={{
              className: 'error-text',
            }}
            inputRef={ref}
            onChange={(e): void => {
              const nextDigits = normalizeUSPhoneDigits(e.target.value);
              onChange(nextDigits);
            }}
            inputProps={{
              ...(props.inputProps || {}),
              inputMode: 'numeric', // mobile numeric keypad
              pattern: '[0-9]*',
              'data-testid': testid,
            }}
          />
        );
      }}
    />
  );
};

export default FormPhoneInput;
